import { useEffect, useMemo, useState } from "react";
import * as S from "./styles.ts";
import { Button, Input, P, Popup, Select } from "@template/components";
import { useTypedSelector } from "@/redux/helpers/hooks.ts";
import { useGetCountryCode } from "@/lib/helpers/useGetCountryCode.ts";

interface ISearchTabProps {
  context: "number" | "country";
  onSearch?: (number: string) => void;
  onSearchByCountry?: (countryCode: string) => void;
  loading: boolean;
}

const isNumber = (input: string) => {
  const numericRegex = /^\d{9,}$/;
  return numericRegex.test(input);
};

const Autocomplete = ({ options, searchValue, setSearchValue, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return [];
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [options, searchValue]);

  const handleChange = (val: string) => {
    setSearchValue(val);
    setIsOpen(val.length > 0);
  };

  const handleSelect = (option: { label: string; key: string }) => {
    setSearchValue(option.label);
    // Send the technical KEY (ISO) back to the parent
    onSelect(option.key);
    setIsOpen(false);
  };

  const getHighlightedText = (text: string, highlight: string) => {
    if (!highlight.trim()) {
      return <span>{text}</span>;
    }

    const parts = text.split(new RegExp(`(${highlight})`, "gi"));

    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === highlight.toLowerCase() ? (
            <S.Highlight key={i}>{part}</S.Highlight>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  return (
    <Popup
      isOpen={isOpen && filteredOptions.length > 0}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      minFullWidth
      unstyled
      popupContent={
        <S.SuggestionList>
          {filteredOptions.map((option, index) => (
            <S.SuggestionItem
              key={index}
              onClick={() => handleSelect(option)}
            >
              {getHighlightedText(option.label, searchValue)}
            </S.SuggestionItem>
          ))}
        </S.SuggestionList>
      }
    >
      <S.Container>
        <Input
          type="text"
          placeholder="Enter Country Name eg. Switzerland"
          value={searchValue}
          onChange={handleChange}
          fullWidth
          hint="Enter a country name to view predicted call costs."
        />
      </S.Container>
    </Popup>
  );
};

export const SearchTab = ({
  context,
  onSearch,
  onSearchByCountry,
  loading,
}: ISearchTabProps) => {
  const siteVersion = useTypedSelector(
    (state) => state.store.site_version.currency
  );
  const countries = useTypedSelector((state) => state.helpers.countries);
  const isGbp = siteVersion === "GBP";

  const [searchValue, setSearchValue] = useState("");
  const [selectedCountryKey, setSelectedCountryKey] = useState<string>("");
  const { countryCode, setCountryCode, availableCountries } =
    useGetCountryCode();

  const handleSearch = async () => {
    const cleanedNumberString = searchValue.replace(/^\++/, "");
    let searchString = "";
    if (context === "number") {
      searchString = `${countryCode}${cleanedNumberString}`;
    } else {
      searchString = `${isGbp ? "44" : "1"}${cleanedNumberString}`;
    }
    onSearch(searchString);
  };

  const handleSearchByCountry = async () => {
    onSearchByCountry(selectedCountryKey);
  };

  const availableCountriesByCountryCode = [
    ...countries.map((country) => ({
      label: `${country.name} `,
      key: country.iso,
    })),
  ];

  useEffect(() => {
    if (availableCountries.length > 0) {
      setCountryCode(availableCountries[0].key);
    }
  }, [availableCountries.length]);

  return (
    <S.SearchTabContainer
      onSubmit={(e) => {
        e.preventDefault();
        context === "number" ? handleSearch() : handleSearchByCountry();
        return false;
      }}
    >
      {context === "number" ? (
        <S.SearchGroup>
          <P>
            Calculate call costs to different local and international numbers
          </P>
          <S.SearchInput>
            <Select
              options={availableCountries}
              variant="solid"
              selected={countryCode}
              onChange={setCountryCode}
              search
              fullWidth
            />
            <Input
              loading={loading}
              hint="Enter a full number to view predicted call costs."
              fullWidth
              value={searchValue}
              placeholder="Enter Full Phone Number eg. 014833040220"
              onChange={(val) => {
                setSearchValue(val);
              }}
            />
          </S.SearchInput>
        </S.SearchGroup>
      ) : (
        <S.SearchGroup>
          <P>Calculate call costs to different countries</P>
          <Autocomplete
            options={availableCountriesByCountryCode}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            onSelect={(key) => setSelectedCountryKey(key)}
          />
        </S.SearchGroup>
      )}
      <S.SearchButton>
        <Button
          size="md"
          type="submit"
        >
          Get Call Costs
        </Button>
      </S.SearchButton>
    </S.SearchTabContainer>
  );
};
